import {FC, useEffect, useRef} from "react";
import IntelligenceEmptyState from "./IntelligenceEmptyState";
import IntelligenceMessage from "./IntelligenceMessage";
import {
    ConversationMessage,
    DiagnosisReport,
    InsightTeaser,
    MessageAction,
    SuggestedPrompt
} from "./intelligenceModels";
import {getAgentIcon} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";
import type {BentoPersona} from "../../bentoPersona";

interface IntelligenceConversationProps {
    messages: ConversationMessage[];
    isThinking: boolean;
    userName: string;
    agent?: AgentIdentity | null;
    persona: BentoPersona;
    insightTeasers: InsightTeaser[];
    isInsightsLoading: boolean;
    onViewAllInsights: () => void;
    suggestions: SuggestedPrompt[];
    isSuggestionDisabled?: (prompt: SuggestedPrompt) => boolean;
    onRunSuggested: (prompt: SuggestedPrompt) => void;
    onFollowUp: (prompt: string) => void;
    onRunAction: (action: MessageAction) => void;
    completedActionIds: string[];
    onLaunchDiagnosisWorkflow: (report: DiagnosisReport) => void;
    /** Whether the drawer is currently maximized — data tables/charts only render when expanded. */
    expanded: boolean;
    promptValue: string;
    promptDisabled: boolean;
    onPromptChange: (value: string) => void;
    onSendPrompt: () => void;
    onSavePrompt: () => void;
}

const IntelligenceConversation: FC<IntelligenceConversationProps> = ({
    messages,
    isThinking,
    userName,
    agent,
    persona,
    insightTeasers,
    isInsightsLoading,
    onViewAllInsights,
    suggestions,
    isSuggestionDisabled,
    onRunSuggested,
    onFollowUp,
    onRunAction,
    completedActionIds,
    onLaunchDiagnosisWorkflow,
    expanded,
    promptValue,
    promptDisabled,
    onPromptChange,
    onSendPrompt,
    onSavePrompt
}) => {
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
    }, [messages, isThinking]);

    const isEmpty = messages.length === 0;
    const AgentIcon = agent ? getAgentIcon(agent) : undefined;

    return (
        <div className="pi-chat__scroll">
            {isEmpty ? (
                <IntelligenceEmptyState
                    userName={userName}
                    agent={agent}
                    persona={persona}
                    insightTeasers={insightTeasers}
                    isInsightsLoading={isInsightsLoading}
                    onViewAllInsights={onViewAllInsights}
                    suggestions={suggestions}
                    isSuggestionDisabled={isSuggestionDisabled}
                    onRunSuggested={onRunSuggested}
                    promptValue={promptValue}
                    promptDisabled={promptDisabled}
                    onPromptChange={onPromptChange}
                    onSendPrompt={onSendPrompt}
                    onSavePrompt={onSavePrompt}
                />
            ) : (
                <div className="pi-chat__messages">
                    {messages.map((message) => (
                        <IntelligenceMessage
                            key={message.id}
                            message={message}
                            agent={agent}
                            onFollowUp={onFollowUp}
                            onRunAction={onRunAction}
                            completedActionIds={completedActionIds}
                            onLaunchDiagnosisWorkflow={onLaunchDiagnosisWorkflow}
                            expanded={expanded}
                        />
                    ))}
                    {isThinking && (
                        <div className="pi-msg pi-msg--assistant">
                            <span
                                className={`pi-msg__avatar${
                                    agent ? " pi-msg__avatar--agent" : ""
                                }`}
                                style={
                                    agent
                                        ? ({
                                              "--par-agent": agent.accent
                                          } as React.CSSProperties)
                                        : undefined
                                }
                            >
                                {AgentIcon && <AgentIcon />}
                            </span>
                            <div className="pi-msg__col">
                                <div className="pi-msg__bubble pi-thinking">
                                    <span className="pi-thinking__dot" />
                                    <span className="pi-thinking__dot" />
                                    <span className="pi-thinking__dot" />
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={endRef} />
                </div>
            )}
        </div>
    );
};

export default IntelligenceConversation;
